/**
 * TransparencyStepsContext — side-channel for passing transparency steps
 * to ChatReasoningTrace without using @assistant-ui data content parts.
 *
 * The legacy runtime adapter (useExternalStoreRuntime + AssistantRuntimeProvider)
 * does not set up the `dataRenderers` scope, so emitting `{ type: 'data' }`
 * content parts crashes MessagePrimitive.Parts.  Instead, providers store
 * steps here and ChatReasoningTrace reads them by message ID.
 *
 * `TransparencyTurnsContext` below is a SIBLING context (bead
 * terra-design-v2-chat-yub.8, Behavior Spec #2), not a widening of this
 * one's value shape — `StepsMap`'s bare-record shape is a public contract
 * read by `SkillStreamRuntimeProvider.tsx` and this file's own hook, and
 * widening it would force edits to both plus the `ChatReasoningTrace.test.tsx`
 * mock. Keeping it a sibling, defaulted to empty, is additive: existing
 * providers and consumers are untouched; only `DurableStreamRuntimeProvider`
 * produces both maps from the one `deriveTransparencyTurns` derivation.
 */

import { createContext, useContext } from 'react';
import type { TransparencyStep } from '../../types/transparency';

export type StepsMap = Record<string, TransparencyStep[]>;

const TransparencyStepsContext = createContext<StepsMap>({});

export const TransparencyStepsProvider = TransparencyStepsContext.Provider;

export function useTransparencySteps(messageId: string | undefined): TransparencyStep[] | null {
  const map = useContext(TransparencyStepsContext);
  if (!messageId) return null;
  return map[messageId] ?? null;
}

/**
 * A per-turn live-reasoning record (Behavior Spec #2). Keyed by `turnId`,
 * scoped to the provider's conversation. The first six fields are the exact
 * public shape bead .8's spec requires — no re-declaration of
 * `StreamMessage` coordinate types (they're copied verbatim, never
 * re-typed). `errored` and `provisionalAssistantMessageId` are additive
 * fields bead terra-design-v2-chat-yub.9 needed beyond that original list —
 * see `deriveTransparencyTurns.ts`'s doc comments for why.
 */
export interface TransparencyTurn {
  turnId: string;
  attempt?: number;
  turnEpoch?: number;
  steps: TransparencyStep[];
  latestStatus: string | null;
  assistantMessageId?: string;
  /** True once an `error` frame carrying this turn's turnId has arrived. */
  errored?: boolean;
  /** The messageId of `useConversationStream.ts`'s synthetic in-progress
   *  assistant message, attributed to this turn by elimination while it is
   *  the only open one. Cleared once `assistantMessageId` is set from a
   *  real association. */
  provisionalAssistantMessageId?: string;
}

export type TurnsMap = Record<string, TransparencyTurn>;

const TransparencyTurnsContext = createContext<TurnsMap>({});

export const TransparencyTurnsProvider = TransparencyTurnsContext.Provider;

/** All turns currently known for this conversation (bead .9's planned
 *  consumer reads this to find the active/latest turn). */
export function useTransparencyTurns(): TurnsMap {
  return useContext(TransparencyTurnsContext);
}

/** A single turn by id; `null` for an absent/undefined lookup (Behavior
 *  Spec #2's null handling) — never throws, never invents a default. */
export function useTransparencyTurn(turnId: string | undefined): TransparencyTurn | null {
  const map = useContext(TransparencyTurnsContext);
  if (!turnId) return null;
  return map[turnId] ?? null;
}

/**
 * bead terra-design-v2-chat-yub.9: the turn still awaiting its own
 * turnId-driven assistant association (`assistantMessageId` unset) —
 * i.e. the in-flight turn `ChatThreadV2`'s turn-placeholder row renders
 * before the real streaming bubble exists. Conversations process asks
 * serially, so at most one such turn exists at a time; returns `null` when
 * none (or, defensively, more than one — ambiguous, so render nothing
 * rather than guess) is open.
 */
export function useOpenTransparencyTurn(): TransparencyTurn | null {
  const map = useContext(TransparencyTurnsContext);
  const open = Object.values(map).filter((t) => !t.assistantMessageId);
  return open.length === 1 ? open[0] : null;
}

/**
 * bead terra-design-v2-chat-yub.9: resolves the `turnId` a given assistant
 * message's live-steps block should read, since the shared `turnsMap` is
 * keyed by `turnId`, not messageId. Tries the finalized/real association
 * first (`assistantMessageId === messageId`); while `messageIsRunning`
 * (this message has no `stats` yet), falls back to the provisional
 * association `deriveTransparencyTurns` attributes to the turnId-less
 * synthetic streaming assistant message (see that file's doc comment) —
 * never while finalized, so a completed, unrelated message never
 * misattributes to a later turn that happens to still be open.
 */
export function useTransparencyTurnIdForMessage(
  messageId: string | undefined,
  messageIsRunning: boolean,
): string | undefined {
  const map = useContext(TransparencyTurnsContext);
  if (!messageId) return undefined;
  for (const turn of Object.values(map)) {
    if (turn.assistantMessageId === messageId) return turn.turnId;
  }
  if (!messageIsRunning) return undefined;
  for (const turn of Object.values(map)) {
    if (turn.provisionalAssistantMessageId === messageId) return turn.turnId;
  }
  return undefined;
}
